import { useEffect, useRef, useState } from "react";
import {includes, lowerCase} from "lodash";


// These are the names of our chatbots in LiveChat. Messages from them
// are not logged.
const ROBOT_ROLL_CALL = [ "otto", "bolt" ];  // lowercase

// Returns { ready: true|false, api: LC_API }

export function liveChatLoaded(api) {
  api ||= window.LC_API;
  return api && (typeof api.open_chat_window === "function");
}

export function useLiveChat() {
  const [ready, setReady] = useState(false);
  const apiRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 100;

    function markReady(api) {
      if (cancelled || initializedRef.current) return;

      initializedRef.current = true;
      apiRef.current = api;
      setReady(true);

      if (intervalId) clearInterval(intervalId);
    }

    function attachOnReady() {
      window.LC_API = window.LC_API || {};
      const api = window.LC_API;

      // If already ready
      if (liveChatLoaded(api)) {
        markReady(api);
        return;
      }

      // Wait for LiveChat callback
      api.on_ready = function () {
        markReady(window.LC_API);
      };
    }

    function pollForApi() {
      intervalId = setInterval(() => {
        if (cancelled || initializedRef.current) {
          clearInterval(intervalId);
          return;
        }

        attempts++;

        const api = window.LC_API;

        if (liveChatLoaded(api)) {
          markReady(api);
          return;
        }

        if (attempts >= MAX_ATTEMPTS) {
          clearInterval(intervalId);
          // Permanently give up
          console.warn("LiveChat failed to load after 100 attempts.");
        }
      }, 100);
    }

    attachOnReady();
    pollForApi();

    return function cleanup() {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return {
    ready,
    api: apiRef.current,
  };
}

export function useLiveChatInit(callback) {
  const { ready, api } = useLiveChat();
  const didRunRef = useRef(false);

  useEffect(() => {
    if (!ready || !api) return;
    if (didRunRef.current) return;

    didRunRef.current = true;

    // callback must return a cleanup function (or undefined)
    return callback(api);
  }, [ready, api, callback]);
}


export function isAgentMessage(message) {
  const agentName = message.agentName || message.agent_name;
  const userType = message.userType || message.user_type;

  if (includes(ROBOT_ROLL_CALL, lowerCase(agentName))) {
    console.log("ignoring robot message");
    return false;
  }

  if (userType === "visitor") {
    console.log("ignoring visitor message");
    return false;
  }

  return true;
}

